/**
 * The size analog (Estimate Tool 6.8, 7.2, 8.4; open questions 1 and 10, answered 2026-09-20).
 *
 * Pure. The ladder comes in as an argument and nothing here reads a database, a clock or a
 * settings row.
 *
 * ## Why there is a third axis at all
 *
 * Section 7.6 is the honest account of what the two rating axes cannot do: they describe how
 * hard a *client* is to work with, neither of them describes how big the *website* is, and the
 * usable pool's gross runs from $200 to $500,102 — a fiftyfold spread between the quartiles that
 * no weighting of client difficulty can explain. Tip the Scale (6.7) was the manual answer to
 * that spread: a lever the estimator pulls after the fact. This is the measured one.
 *
 * Open question 10 asked whether the account's own Microsite / Small / Medium / Large /
 * Enterprise ladder is the right scope tier, in preference to the hours-derived small/medium/
 * large that `historical_project_metrics` already computes. It is: it is how the account
 * actually thinks about website size, it is curated by the people who run the work, and it is
 * already the ladder the schedule generator cuts from — so one choice on the estimate now feeds
 * both halves of the tool rather than each half having its own idea of how big the job is.
 *
 * ## Which is why the rung is a *position*, not a bucket
 *
 * The five tiers are ordered and evenly spaced, so the distance between two of them is the
 * number of rungs between them over the four that exist. A Microsite and an Enterprise site are
 * as far apart as the axis goes; a Medium and a Large are a quarter of that. Bucketing — same
 * tier or not — would throw away the only thing an ordered ladder knows.
 */

/**
 * The ladder, smallest first (Estimate Tool 8.4). The order **is** the scale: the index of a
 * tier in this array is its position on the size axis, and `SIZE_SPAN` is the distance from end
 * to end.
 *
 * This is the one definition. `SCHEDULE_TIERS` in the settings module is this array, re-exported,
 * because the tier that narrows the analog pool and the tier that picks a schedule template have
 * to be the same five words — an estimate whose size said `large` and whose schedule cut from
 * `enterprise` would be two answers to one question.
 */
export const SIZE_TIERS = Object.freeze([
  'landing_page',
  'microsite',
  'small',
  'medium',
  'large',
  'enterprise',
]);

/** One less than the number of rungs: the distance end to end. */
export const SIZE_SPAN = SIZE_TIERS.length - 1;

/** The rungs in the words a screen uses. Only the underscored one needs saying. */
export const SIZE_TIER_LABELS = Object.freeze({ landing_page: 'landing page' });

/** A rung in the words a screen uses. */
export const sizeTierLabel = (tier) => SIZE_TIER_LABELS[tier] ?? tier;

/**
 * Where a rung sits on the size axis, in hours (Estimate Tool 7.2, 8.10).
 *
 * A **stated range** wins, at its geometric mean — the ladder is compared in log space, so the
 * middle of a range is its geometric middle (√(4 × 16) = 8, not 10). Otherwise the template's own
 * total. Null when neither is known, which drops the rung from the ladder rather than guessing a
 * position for it.
 *
 * Until 2026-09-20 one rung stated a range in code (`SIZE_TIER_RANGES`, Landing Page at 4-16 h,
 * because its template's 27.5 h would have put the boundary with Microsite in the wrong place).
 * Since E13 every rung carries its range on the template row, seeded at its total, and the
 * designer is where it is changed: once templates are edited by hand, an hour added to the
 * Medium template's content audit must not move the Medium–Large boundary and re-weight every
 * analog set in the tool from a screen somebody thought was about a schedule.
 *
 * **So there are still two ladders, and confusing them is the bug this comment exists to
 * prevent.** The *size* ladder positions rungs on the similarity axis — the stated ranges. The
 * *template* ladder is what each published version actually holds — what a schedule is cut from
 * and what `recommendTier` walks. They answer different questions and they are allowed to
 * disagree.
 *
 * @param {{ min: number, max: number }|null|undefined} range
 * @param {number|null|undefined} templateHours
 */
export function anchorHours(range, templateHours) {
  if (range && Number(range.min) > 0 && Number(range.max) >= Number(range.min)) {
    return Math.sqrt(Number(range.min) * Number(range.max));
  }
  const h = Number(templateHours);
  return Number.isFinite(h) && h > 0 ? h : null;
}

/** What a rung's size is worth saying as: a stated range, or the template's total. */
export function anchorLabel(range, templateHours) {
  if (range && Number(range.min) > 0 && Number(range.max) >= Number(range.min)) {
    const { min, max } = range;
    return Number(min) === Number(max) ? `about ${fmt(min)} h` : `${fmt(min)}–${fmt(max)} h`;
  }
  const h = Number(templateHours);
  return Number.isFinite(h) && h > 0 ? `about ${fmt(h)} h` : null;
}

const fmt = (n) => String(Math.round(Number(n) * 100) / 100);

/**
 * The range a rung's template row is **seeded** with when the row is first made, where a rung
 * has one to state (Estimate Tool 7.2, 8.10). Every other rung is seeded at its first published
 * total. Landing Page is the one that cannot be: its template budgets 27.5 h and the account's
 * actual landing pages book 2.25 to 22, so positioning it at the template would put the boundary
 * with Microsite at 68 h and start calling small microsites landing pages. The E13 migration
 * seeds the same numbers in SQL and a test pins the two together. From there the range lives on
 * the row and the designer is where it changes; this is a default, not a definition.
 */
export const SEED_SIZE_RANGES = Object.freeze({
  landing_page: Object.freeze({ min: 4, max: 16 }),
});

/** A tier's position on the ladder, or null for anything that is not one of the six. */
export function tierIndex(tier) {
  const i = SIZE_TIERS.indexOf(tier);
  return i === -1 ? null : i;
}

/** Whether a value names a rung. */
export const isSizeTier = (tier) => tierIndex(tier) !== null;

/**
 * How far apart two tiers are, in [0, 1], or null when either is unknown.
 *
 * Null rather than a number, always: an unknown size is a *missing* measurement, and the whole
 * point of returning null is that the caller has to decide what to do about it rather than
 * being handed a distance that was invented for it.
 */
export function sizeDistance(a, b) {
  const ia = tierIndex(a);
  const ib = tierIndex(b);
  if (ia === null || ib === null) return null;
  return Math.abs(ia - ib) / SIZE_SPAN;
}

/**
 * The rung a number of hours lands on, against the ladder's own totals.
 *
 * **Nearest in log space, not in hours.** The ladder is roughly geometric — 168, 433, 979,
 * 1507, 1935 — so a linear nearest-neighbour would put the boundaries in the wrong places: 800
 * hours is 367 above Small and 179 below Medium, and reads as Medium either way, but 300 hours
 * is 132 above Microsite and 133 below Small, which a linear rule calls Microsite by one hour.
 * The ratio is the question actually being asked — "how many times a Microsite is this?" — and
 * the log distance is the ratio. It is the same rule `recommendTier` uses to pick a template,
 * and it is the same rule for the same reason.
 *
 * Returns null for a project with no hours. 83 of the account's 202 website projects carry
 * none, and calling all of them Microsites would put a third of the pool on the bottom rung and
 * tell the model something nobody knows.
 *
 * @param {number|null|undefined} hours
 * @param {{ tier: string, hours: number }[]} ladder
 * @returns {{ tier: string, template_hours: number, ratio: number } | null}
 */
export function tierForHours(hours, ladder) {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return null;
  const usable = (ladder ?? []).filter(
    (t) => isSizeTier(t.tier) && Number.isFinite(Number(t.hours)) && Number(t.hours) > 0,
  );
  if (!usable.length) return null;

  let best = null;
  for (const t of usable) {
    const templateHours = Number(t.hours);
    const distance = Math.abs(Math.log(h / templateHours));
    if (!best || distance < best.distance) {
      best = { tier: t.tier, template_hours: templateHours, ratio: h / templateHours, distance };
    }
  }
  return { tier: best.tier, template_hours: best.template_hours, ratio: best.ratio };
}

/**
 * A project's size, and where it came from.
 *
 * Two sources and one rule, and the rule is the opposite of the ratings' one (9.3): **a size set
 * by hand wins over one derived from hours.** The two cases are not alike. A client's rating in
 * Workamajig is a record somebody maintains, so the tool defers to it. A derived size is an
 * inference this tool made from a number that may be a fraction of the truth — a project whose
 * hours were booked to the wrong job reads small, and the person correcting it knows something
 * the arithmetic cannot.
 *
 * @param {{ hours?: number|null, local_size_tier?: string|null }} project
 * @param {{ tier: string, hours: number }[]} ladder
 * @returns {{ size_tier: string|null, size_source: 'set'|'hours'|null,
 *             derived_size_tier: string|null }}
 */
export function projectSize(project, ladder) {
  const derived = tierForHours(project?.hours, ladder);
  const derivedTier = derived?.tier ?? null;
  const set = isSizeTier(project?.local_size_tier) ? project.local_size_tier : null;
  if (set) return { size_tier: set, size_source: 'set', derived_size_tier: derivedTier };
  if (derivedTier)
    return { size_tier: derivedTier, size_source: 'hours', derived_size_tier: derivedTier };
  return { size_tier: null, size_source: null, derived_size_tier: null };
}
